import {
  Activity,
  Bell,
  BookOpen,
  ChevronDown,
  CircleUserRound,
  Gamepad2,
  Gavel,
  Home,
  Menu,
  Radio,
  Scale,
  ScrollText,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Trophy,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "../router";
import { useAuth } from "../auth/AuthProvider";
import { useRealtime } from "../realtime/RealtimeProvider";
import { Brand } from "./Brand";
import { NotificationsPanel } from "./NotificationsPanel";
import { Avatar, Money, SafeText } from "./ui";

interface NavEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const primary: NavEntry[] = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/aktive-games", label: "Aktive Games", icon: Radio },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/games", label: "Games", icon: Gamepad2 },
  { to: "/marketplace", label: "Marketplace", icon: ShoppingBag },
  { to: "/abstimmungen", label: "Abstimmungen", icon: Scale },
  { to: "/account", label: "Account", icon: CircleUserRound },
  { to: "/log", label: "Log", icon: ScrollText },
];

function Navigation({ onNavigate }: { onNavigate?: () => void }) {
  const auth = useAuth();
  const team =
    auth.state.kind === "authenticated" &&
    (auth.state.viewer.roles.includes("ADMIN") || auth.state.viewer.roles.includes("MODERATOR"));
  const admin = auth.state.kind === "authenticated" && auth.state.viewer.roles.includes("ADMIN");
  return (
    <nav className="main-nav" aria-label="Hauptnavigation">
      <span className="main-nav__label">Spielbereich</span>
      {primary.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          {...(end ? { end: true } : {})}
          {...(onNavigate ? { onClick: onNavigate } : {})}
        >
          <Icon aria-hidden="true" size={19} />
          <span>{label}</span>
        </NavLink>
      ))}
      {team && (
        <>
          <span className="main-nav__label">Team</span>
          <NavLink to="/team" onClick={onNavigate}>
            <Users aria-hidden="true" size={19} />
            <span>Moderation</span>
          </NavLink>
        </>
      )}
      {admin && (
        <NavLink to="/admin" onClick={onNavigate}>
          <ShieldCheck aria-hidden="true" size={19} />
          <span>Admin</span>
        </NavLink>
      )}
    </nav>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const realtime = useRealtime();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const viewer = auth.state.kind === "authenticated" ? auth.state.viewer : null;

  useEffect(() => {
    setMobileOpen(false);
    setNotificationsOpen(false);
    setAccountOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  if (!viewer) return <>{children}</>;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Zum Inhalt springen
      </a>
      <aside className="sidebar">
        <Brand />
        <Navigation />
        <div className="sidebar__footer">
          <Link to="/android">
            <Smartphone aria-hidden="true" size={16} />
            Android-App
          </Link>
          <Link to="/rechtliches">
            <BookOpen aria-hidden="true" size={16} />
            Rechtliches
          </Link>
          <span>
            <i className={`connection-dot connection-dot--${realtime.state}`} />
            Live: {realtime.state === "online" ? "verbunden" : "getrennt"}
          </span>
        </div>
      </aside>

      <header className="topbar">
        <button
          type="button"
          className="icon-button topbar__menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          aria-label="Navigation öffnen"
          onClick={() => setMobileOpen((open) => !open)}
        >
          {mobileOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
        <div className="topbar__mobile-brand">
          <Brand compact />
        </div>
        <div className="topbar__spacer" />
        <Link
          className="wallet-pill"
          to="/account/wallet"
          aria-label={`Wallet: ${viewer.balanceMinor / 100} Bucks`}
        >
          <Money value={viewer.balanceMinor} compact />
        </Link>
        <button
          type="button"
          className="icon-button notification-button"
          aria-expanded={notificationsOpen}
          aria-label={`${viewer.unreadNotifications} ungelesene Benachrichtigungen`}
          onClick={() => setNotificationsOpen((open) => !open)}
        >
          <Bell aria-hidden="true" />
          {viewer.unreadNotifications > 0 && (
            <span className="notification-count">{Math.min(viewer.unreadNotifications, 99)}</span>
          )}
        </button>
        <div className="account-menu" ref={accountRef}>
          <button
            type="button"
            className="account-menu__trigger"
            aria-expanded={accountOpen}
            onClick={() => setAccountOpen((open) => !open)}
          >
            <Avatar name={viewer.username} src={viewer.avatarUrl} size="sm" />
            <span>
              <SafeText>{viewer.username}</SafeText>
              <small>
                {viewer.roles.includes("ADMIN")
                  ? "Admin"
                  : viewer.roles.includes("MODERATOR")
                    ? "Moderator"
                    : "Spieler"}
              </small>
            </span>
            <ChevronDown aria-hidden="true" size={16} />
          </button>
          {accountOpen && (
            <div className="account-menu__popover">
              <Link to="/account">Profil & Sicherheit</Link>
              <Link to="/account/sitzungen">Aktive Sitzungen</Link>
              <button type="button" onClick={() => void auth.logout()}>
                Abmelden
              </button>
            </div>
          )}
        </div>
      </header>

      {mobileOpen && (
        <aside id="mobile-menu" className="mobile-drawer">
          <Navigation onNavigate={() => setMobileOpen(false)} />
          <div className="mobile-drawer__footer">
            <Link to="/android">
              <Smartphone aria-hidden="true" size={17} />
              Android-App
            </Link>
            <Link to="/rechtliches">
              <BookOpen aria-hidden="true" size={17} />
              Rechtliches
            </Link>
          </div>
        </aside>
      )}
      {mobileOpen && (
        <button
          className="drawer-backdrop"
          type="button"
          aria-label="Navigation schließen"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <main id="main-content" className="main-content" tabIndex={-1}>
        {viewer.securityNotice && (
          <aside className="security-alert" role="alert" aria-live="assertive">
            <div>
              <strong>Sicherheitswarnung</strong>
              <span>{viewer.securityNotice.message}</span>
            </div>
            <nav aria-label="Alternativcode verwalten">
              <Link
                className="button button--secondary"
                to={`/account?codeAction=single&position=${viewer.securityNotice.position}#admin-alternative-codes`}
              >
                Verwendeten Code ändern
              </Link>
              <Link
                className="button button--danger"
                to="/account?codeAction=all#admin-alternative-codes"
              >
                Alle Codes neu erzeugen
              </Link>
              <button
                className="button button--ghost"
                type="button"
                onClick={auth.dismissSecurityNotice}
              >
                Unverändert lassen
              </button>
            </nav>
          </aside>
        )}
        {children}
      </main>

      <nav className="mobile-tabs" aria-label="Mobile Schnellnavigation">
        {[
          { to: "/", label: "Home", icon: Home, end: true },
          { to: "/aktive-games", label: "Live", icon: Activity },
          { to: "/games", label: "Games", icon: Gamepad2 },
          { to: "/marketplace", label: "Markt", icon: Gavel },
          { to: "/account", label: "Account", icon: CircleUserRound },
        ].map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} {...(end ? { end: true } : {})}>
            <Icon aria-hidden="true" size={19} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {notificationsOpen && <NotificationsPanel onClose={() => setNotificationsOpen(false)} />}
    </div>
  );
}
